import { selectRichTextFocus } from "Redux/ProducerSlice";
import {
  selectUserLinks,
  selectUserRefs,
  selectUserTerms,
} from "Redux/UserSlice";
import { useAppSelector } from "Redux/hooks";
import { useEffect, useState } from "react";
import Select from "react-select";
import { ElementType } from "types/types";
import RichTextTranslator from "../../Logic/RichTextTranslator";

/**
   @function ElementSelector
   renders a selectors for all the different types of elements
   @param content {object} - content to render
*/
export default function ElementSelector({
  content,
  onUpdate,
}: {
  content: any;
  onUpdate: (value: string) => void;
}) {
  const richTextFocus = useAppSelector(selectRichTextFocus);
  const [terms, setTerms] = useState<any[]>([]);
  const [links, setLinks] = useState<any[]>([]);
  const [refs, setRefs] = useState<any[]>([]);

  let richText = content;

  if (typeof richText === "string") {
    richText = JSON.parse(richText);
  }

  useEffect(() => {
    const termsShort: Array<any> = [];
    const linksShort: Array<any> = [];
    const refsShort: Array<any> = [];

    const originalText =
      "text" in richText
        ? {
            type: "doc",
            content: RichTextTranslator({
              text: richText.text,
            }),
          }
        : JSON.parse(JSON.stringify(richText));

    searchElement(
      originalText,
      { element: "term", data: "data-term-id" },
      (element) => termsShort.push(element),
      originalText
    );

    searchElement(
      originalText,
      { element: "web-link", data: "data-web-link-id" },
      (element) => linksShort.push(element),
      originalText
    );

    searchElement(
      originalText,
      { element: "ref", data: "data-ref-id" },
      (element) => refsShort.push(element),
      originalText
    );

    if (termsShort.length !== terms.length) {
      setTerms(termsShort);
    }

    if (linksShort.length !== links.length) {
      setLinks(linksShort);
    }

    if (refsShort.length !== refs.length) {
      setRefs(refsShort);
    }
  }, [terms, links, refs, content]);

  useEffect(() => {
    setTerms([]);
    setLinks([]);
    setRefs([]);
  }, [
    richTextFocus?.chapter,
    richTextFocus?.question,
    richTextFocus?.answer,
    content,
  ]);

  return (
    <div>
      <h2 className="text-xl">Terms</h2>
      <HandleElements
        elements={terms}
        type={ElementType.Term}
        onUpdate={onUpdate}
      />
      <h2 className="text-xl">Links</h2>
      <HandleElements
        elements={links}
        type={ElementType.Link}
        onUpdate={onUpdate}
      />
      <h2 className="text-xl">References</h2>
      <HandleElements
        elements={refs}
        type={ElementType.Reference}
        onUpdate={onUpdate}
      />
    </div>
  );
}

/**
   @function handleElements
   handle elements to change
   @param elements {array} - elements to change
   @param type {string} - type of element
   @returns {React.Node}
*/
function HandleElements({
  elements,
  type,
  onUpdate,
}: {
  elements: any[];
  type: ElementType;
  onUpdate: (value: string) => void;
}) {
  const userLinks = useAppSelector(selectUserLinks);
  const userTerms = useAppSelector(selectUserTerms);
  const userRefs = useAppSelector(selectUserRefs);

  const resolveData = () => {
    switch (type) {
      case ElementType.Link:
        return userLinks;

      case ElementType.Term:
        return userTerms;

      case ElementType.Reference:
        return userRefs;
    }
  };

  const data = resolveData();

  if (!data) return null;

  const toShow = Object.keys(data).sort((a, b) =>
    type === ElementType.Reference
      ? // @ts-ignore
        data[a].code.localeCompare(data[b].code)
      : // @ts-ignore
        data[a].name.localeCompare(data[b].name)
  );

  return (
    <>
      {elements
        .filter((e) => e.id !== undefined)
        .map((e, i) => (
          <div key={i} className="flex flex-col mb-4">
            <div>{e.text}</div>
            <div>
              <Select
                // @ts-ignore
                onChange={(event) => onUpdate(e.callback(event?.value))}
                defaultValue={
                  type === ElementType.Reference
                    ? e.id !== "" && e.id !== undefined
                      ? // @ts-ignore
                        { value: e.id, label: data[e.id].code }
                      : "choose reference"
                    : {
                        value: e.id,
                        label: data[e.id]
                          ? // @ts-ignore
                            data[e.id].name
                          : "could not find element",
                      }
                }
                options={
                  data !== undefined
                    ? toShow.map((a) => {
                        return {
                          value: a,
                          label:
                            type === ElementType.Reference
                              ? // @ts-ignore
                                data[a].code
                              : // @ts-ignore
                                data[a].name,
                        };
                      })
                    : []
                }
              />
            </div>
          </div>
        ))}
    </>
  );
}

/**
   @function searchElement
   search element in Rich text
   @param text {object} - text to search
   @param key {string} - key to searchuser.ref[r.id].code
   @param addElement {function} - way to add elements
*/
export function searchElement(
  text: any,
  key: {
    element: string;
    data: string;
  },
  addElement: (element: any) => void,
  originalText: Record<string, string>
) {
  if (text.marks !== undefined) {
    text.marks.forEach((m: any) => {
      if (m.type === key.element) {
        addElement({
          text: text.text,
          id: m.attrs[key.data],
          callback: (id: any) => {
            m.attrs[key.data] = id;
            return originalText;
          },
          textToChange: originalText,
        });
      }
    });
  }

  if (text.content !== undefined) {
    return text.content.map((c: any) =>
      searchElement(c, key, addElement, originalText)
    );
  }
}

/**
   @function searchElementModifyText
   search element in Rich text and modify text
   @param text {object} - text to search
   @param key {string} - key to search
   @param addElement {function} - way to add elements
*/
export function searchElementModifyText(
  text: any,
  key: {
    element: string;
    data: string;
  },
  addElement: (element: any) => void,
  originalText: Record<string, string>
) {
  if (text.marks !== undefined) {
    text.marks.forEach((m: any) => {
      if (m.type === key.element) {
        addElement({
          text: text.text,
          id: m.attrs[key.data],
          callback: (newText: any) => {
            text.text = newText;
            return originalText;
          },
          textToChange: originalText,
        });
      }
    });
  }

  if (text.content !== undefined) {
    return text.content.map((c: any) =>
      searchElementModifyText(c, key, addElement, originalText)
    );
  }
}
